import { useState } from 'react';
import { Button, HStack, Stack, Text } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { toaster } from '@/components/ui/toaster';
import useApiKeysStore from '@/utils/use-api-keys';
import { ApiKey } from '@/lib/types';
import ApiKeySecretReveal from './api-key-secret-reveal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  apiKey: ApiKey | null;
}

export default function RotateApiKeyDialog({ isOpen, onClose, apiKey }: Props) {
  const { workspaceId } = useParams();
  const rotateApiKey = useApiKeysStore((s) => s.rotateApiKey);
  const [secret, setSecret] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function handleClose() {
    setSecret(null);
    setSubmitting(false);
    onClose();
  }

  async function handleRotate() {
    if (!apiKey || !workspaceId) return;
    setSubmitting(true);
    try {
      const created = await rotateApiKey(workspaceId, apiKey.id);
      setSecret(created.key);
      toaster.create({ type: 'success', title: 'API key rotated' });
    } catch (e: any) {
      toaster.create({
        type: 'error',
        title: 'Could not rotate key',
        description: e?.response?.data?.error,
      });
      setSubmitting(false);
    }
  }

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={(d) => (!d.open ? handleClose() : null)}>
      <DialogBackdrop />
      <DialogContent>
        <DialogCloseTrigger />
        {secret === null ? (
          <>
            <DialogHeader>
              <DialogTitle>Rotate API key</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <Stack gap={3}>
                <Text fontSize='sm'>
                  Rotating generates a fresh secret and immediately revokes the current one.
                  Any application still using the old key will stop working.
                </Text>
                {apiKey && (
                  <Text fontSize='sm' color='fg.muted'>
                    The new key will keep the same name (<Text as='span' fontWeight='medium'>{apiKey.name}</Text>),
                    scopes, and expiration.
                  </Text>
                )}
              </Stack>
            </DialogBody>
            <DialogFooter>
              <HStack>
                <Button variant='ghost' onClick={handleClose} type='button'>
                  Cancel
                </Button>
                <Button variant='danger' onClick={handleRotate} loading={submitting}>
                  Rotate key
                </Button>
              </HStack>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Copy your new API key</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <ApiKeySecretReveal secret={secret} />
            </DialogBody>
            <DialogFooter>
              <Button colorPalette='brand' onClick={handleClose}>
                I&apos;ve saved it
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </DialogRoot>
  );
}
